import { Plus } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { NEW_CHAT_SHORTCUT_LABEL } from '../new-chat-button';

interface NewChatButtonLegacyProps {
  onClick: () => void;
  /** `sm` is the activity board footer's compact variant — 30px, no
   *  shortcut badge (README §"Chat activity board" footer). */
  size?: 'default' | 'sm';
  className?: string;
}

/**
 * FRONT-3223: the New chat button as on `origin/main`, for the
 * `eddy.ui-refactoring` OFF path — it still carries the `sm` variant the legacy
 * activity board's footer renders, which the reworked button
 * (`../new-chat-button.tsx`) dropped along with that footer. The shortcut
 * label is shared with the reworked button so the badge can never disagree
 * with the ⌥N handler.
 */
export function NewChatButtonLegacy({ onClick, size = 'default', className }: NewChatButtonLegacyProps) {
  if (size === 'sm') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex h-[30px] shrink-0 items-center gap-[7px] rounded-[9px] bg-[var(--eddy-blue)] px-[11px]',
          'text-[13px] font-medium whitespace-nowrap text-white transition-colors hover:bg-[var(--eddy-blue-hover)]',
          className,
        )}
      >
        <Plus size={12} />
        New chat
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // 38 matches the status cluster and the ask chip exactly, so the
        // whole left group reads as one row.
        'eddy-chat-btn inline-flex h-[38px] shrink-0 items-center gap-[10px] rounded-[11px] bg-[var(--eddy-blue)] px-[18px]',
        'text-[16px] font-semibold tracking-[-0.015em] whitespace-nowrap text-white',
        'shadow-[0_5px_14px_-6px_rgba(41,98,255,0.7)] transition-[background-color,box-shadow] duration-150',
        'hover:bg-[var(--eddy-blue-hover)] hover:shadow-[0_8px_20px_-8px_rgba(41,98,255,0.85)]',
        className,
      )}
    >
      <Plus size={15} />
      {/* "Chat", not "New chat": the plus glyph already says "new", and the
       *  button sits beside a "Build" one. The compact `sm` variant in the
       *  board footer deliberately KEEPS "New chat": it sits under a list of
       *  existing chats, where a bare "Chat" would be ambiguous. */}
      Chat
      <span className="eddy-kbd rounded-[6px] border border-white/25 bg-white/20 px-[6px] py-[2px] font-mono text-xs leading-tight font-medium text-white">
        {NEW_CHAT_SHORTCUT_LABEL}
      </span>
    </button>
  );
}
